// src/features/task/panelHelpers.ts
// Small framework-agnostic helpers shared by the task detail-rail panels and
// the task admin surfaces. Zero React — pure TypeScript.

import type { Entity } from '@core/types/entity';

export interface ScopeLike {
  productId: string;
  platformId: string;
  tenantId: string;
}

/** Build a minimal Entity for request payloads from an id + type + scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeLike): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

/** Extract a human-readable message from an unknown thrown value. */
export function errMsg(e: unknown, fallback: string): string {
  if (e && typeof e === 'object') {
    const anyE = e as { message?: unknown; body?: { message?: unknown } };
    if (typeof anyE.message === 'string' && anyE.message) return anyE.message;
    if (anyE.body && typeof anyE.body.message === 'string') return anyE.body.message;
  }
  if (typeof e === 'string' && e) return e;
  return fallback;
}

/** Parse a duration string (protobuf "3600s" or ISO-ish) to whole seconds. */
export function durationToSeconds(d: string | number | undefined | null): number {
  if (d == null) return 0;
  if (typeof d === 'number') return d;
  const m = String(d).match(/^(\d+(?:\.\d+)?)s?$/);
  if (m) return Math.round(parseFloat(m[1]));
  return 0;
}

/** Serialize whole seconds to the protobuf duration string form ("5400s"). */
export function secondsToDuration(seconds: number): string {
  return `${Math.max(0, Math.round(seconds))}s`;
}

/** Convert an "Nh Mm" style input pair (hours, minutes) to a duration string. */
export function hoursMinutesToDuration(hours: number, minutes: number): string {
  return secondsToDuration(hours * 3600 + minutes * 60);
}

/** Format whole seconds as "1h 30m" / "45m" / "0m". */
export function formatSeconds(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

/** Sum the `duration` fields of a list of time entries into total seconds. */
export function totalSeconds(entries: Array<{ duration?: string | number }>): number {
  return entries.reduce((acc, e) => acc + durationToSeconds(e.duration), 0);
}

/** Only digits — used so numeric inputs stay clearable and get no steppers. */
export function digitsOnly(v: string): string {
  return v.replace(/[^0-9]/g, '');
}

/** ISO string for a `datetime-local` value (or now when empty). */
export function localToIso(local: string): string {
  if (!local) return new Date().toISOString();
  const d = new Date(local);
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

/** `datetime-local` value from an ISO string. */
export function isoToLocal(iso: string | undefined | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const DEPENDENCY_TYPES = [
  'DEPENDENCY_TYPE_BLOCKED_BY',
  'DEPENDENCY_TYPE_BLOCKS',
  'DEPENDENCY_TYPE_RELATES_TO',
  'DEPENDENCY_TYPE_DUPLICATES',
  'DEPENDENCY_TYPE_PARENT_OF',
] as const;

export const CUSTOM_FIELD_TYPES = [
  'CUSTOM_FIELD_TYPE_TEXT',
  'CUSTOM_FIELD_TYPE_NUMBER',
  'CUSTOM_FIELD_TYPE_DATE',
  'CUSTOM_FIELD_TYPE_DATETIME',
  'CUSTOM_FIELD_TYPE_SELECT',
  'CUSTOM_FIELD_TYPE_MULTI_SELECT',
  'CUSTOM_FIELD_TYPE_USER',
  'CUSTOM_FIELD_TYPE_ENTITY',
  'CUSTOM_FIELD_TYPE_URL',
  'CUSTOM_FIELD_TYPE_CHECKBOX',
] as const;

export const NUMBERING_PATTERNS = [
  'TASK_NUMBERING_PATTERN_SEQUENTIAL',
  'TASK_NUMBERING_PATTERN_YEAR_SEQUENTIAL',
  'TASK_NUMBERING_PATTERN_CATEGORY_SEQUENTIAL',
  'TASK_NUMBERING_PATTERN_YEAR_CATEGORY',
  'TASK_NUMBERING_PATTERN_CUSTOM',
] as const;

export const NUMBERING_RESET_STRATEGIES = [
  'TASK_NUMBERING_RESET_STRATEGY_NEVER',
  'TASK_NUMBERING_RESET_STRATEGY_YEARLY',
  'TASK_NUMBERING_RESET_STRATEGY_QUARTERLY',
  'TASK_NUMBERING_RESET_STRATEGY_MONTHLY',
] as const;

/** Sensible fixed list of entity types a scope/container/parent field can point at. */
export const SCOPE_ENTITY_TYPES = [
  'project',
  'team',
  'department',
  'tenant',
  'category',
  'pipeline',
] as const;

/**
 * Build a field_mask.paths array of the top-level fields that differ between
 * `original` and `updated`. Used by admin editors to send a diff-aware
 * field_mask instead of overwriting every field on every save.
 */
export function diffFields<T>(
  original: T | null | undefined,
  updated: T,
  fields: (keyof T & string)[],
): string[] {
  const before = (original || {}) as Partial<Record<keyof T, unknown>>;
  const after = updated as Partial<Record<keyof T, unknown>>;
  return fields.filter((f) => JSON.stringify(before[f]) !== JSON.stringify(after[f]));
}

/** Zero-pad a counter to `digits` width. */
function padCounter(n: number, digits: number): string {
  return String(Math.max(0, n)).padStart(Math.max(1, digits), '0');
}

/**
 * Compute a sample formatted number for a numbering scheme, used for the live
 * preview in the numbering admin form and the scheme list rows.
 * When `format_template` is set, simple `{prefix}` / `{separator}` / `{counter}`
 * / `{suffix}` / `{year}` placeholders are substituted; otherwise falls back to
 * `prefix + separator + zero-padded counter + suffix`.
 */
export function previewNumberingSample(input: {
  prefix?: string;
  separator?: string;
  counter_digits?: number;
  suffix?: string;
  format_template?: string;
  next_number?: number;
}): string {
  const prefix = input.prefix || '';
  const sep = input.separator ?? '-';
  const digits = input.counter_digits ?? 4;
  const counter = padCounter(input.next_number && input.next_number > 0 ? input.next_number : 1, digits);
  const suffix = input.suffix || '';

  if (input.format_template && input.format_template.trim()) {
    return input.format_template
      .replace(/\{prefix\}/gi, prefix)
      .replace(/\{separator\}/gi, sep)
      .replace(/\{sep\}/gi, sep)
      .replace(/\{counter\}/gi, counter)
      .replace(/\{number\}/gi, counter)
      .replace(/\{suffix\}/gi, suffix)
      .replace(/\{year\}/gi, String(new Date().getFullYear()));
  }
  return `${prefix}${sep}${counter}${suffix}`;
}
